<template>
  <b-modal :active="active" @close="$emit('close')" has-modal-card trap-focus aria-role="dialog" aria-modal
           :width="'90%'">
    <div class="modal-card" style="width: 80vw; margin: auto;">
      <header class="modal-card-head">
        <p class="modal-card-title has-text-weight-bold">Delete Videos Report</p>
        <button class="delete" aria-label="close" @click="$emit('close')"></button>
      </header>
      <section class="modal-card-body">
        <b-loading :active="isLoading" :is-full-page="false"></b-loading>

        <div v-if="report && report.length > 0">
          <div class="columns is-multiline">
            <div class="column is-3" v-for="camera in report" :key="camera.id">
              <div v-if="selections[camera.id]" class="box p-4 camera-card"
                   :class="{ 'camera-card--selected': isSelected(camera.id) }"
                   @click="toggleCamera(camera)">
                <div class="is-size-6 has-text-weight-semibold mb-3">{{ camera.name }}</div>
                <div class="is-flex is-align-items-center is-size-7 mb-1" @click.stop>
                  <b-checkbox v-model="selections[camera.id].raw"
                              :disabled="camera.rawCount === 0"
                              @input="onCheckboxChange(camera)">
                    Raw videos
                  </b-checkbox>
                  <span class="has-text-weight-semibold ml-auto">{{ camera.rawCount }}</span>
                </div>
                <div class="is-flex is-align-items-center is-size-7" @click.stop>
                  <b-checkbox v-model="selections[camera.id].conditioned"
                              :disabled="camera.conditionedCount === 0"
                              @input="onCheckboxChange(camera)">
                    Conditioned videos
                  </b-checkbox>
                  <span class="has-text-weight-semibold ml-auto">{{ camera.conditionedCount }}</span>
                </div>
                <div class="is-size-7 mt-2 has-text-grey">
                  <b-icon icon="earth" size="is-small" class="mr-1"></b-icon>
                  {{ camera.timezones && camera.timezones.length > 0 ? camera.timezones.join(', ') : 'Unknown' }}
                </div>
              </div>
            </div>
          </div>

          <hr>

          <div class="mb-4">
            <div class="is-flex is-align-items-center mb-3">
              <span class="is-size-6 has-text-weight-semibold">Date Ranges</span>
              <b-button size="is-small" icon-left="plus" class="ml-auto" @click="addDateRange">
                Add Range
              </b-button>
            </div>

            <div v-for="(range, index) in dateRanges" :key="index" class="is-flex is-align-items-center mb-2"
                 style="gap: 8px;">
              <b-datetimepicker
                  v-model="range.start"
                  placeholder="Start"
                  icon="calendar-clock"
                  editable
                  size="is-small"
                  horizontal-time-picker
                  hour-format="24">
              </b-datetimepicker>
              <span class="has-text-grey">to</span>
              <b-datetimepicker
                  v-model="range.end"
                  placeholder="End"
                  icon="calendar-clock"
                  editable
                  size="is-small"
                  horizontal-time-picker
                  hour-format="24">
              </b-datetimepicker>
              <b-button size="is-small" icon-left="close" type="is-danger" outlined @click="removeDateRange(index)">
              </b-button>
            </div>
          </div>
        </div>

        <div v-else-if="!isLoading" class="has-text-centered has-text-grey py-5">
          No report data available.
        </div>
      </section>
      <footer class="modal-card-foot is-flex is-justify-content-flex-end">
        <b-button @click="$emit('close')">Cancel</b-button>
        <b-button type="is-info" icon-left="filter"
                  @click="onCheckVideos">
          Filter Videos
        </b-button>
        <view-guard :permission="'data-processing:delete-videos'">
          <b-button type="is-danger" icon-left="delete-forever" :disabled="!hasSelectedCameras"
                    @click="onDelete">
            Delete
          </b-button>
        </view-guard>
      </footer>
    </div>
  </b-modal>
</template>

<script>
import ViewGuard from "@/components/view-guard/view-guard.vue";

export default {
  name: "delete-videos-dialog",
  components: {ViewGuard},
  props: {
    active: {
      type: Boolean,
      default: false
    },
    report: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dateRanges: [],
      selections: {}
    }
  },
  computed: {
    hasValidDateRanges() {
      return this.dateRanges.some(r => r.start && r.end)
    },
    hasSelectedCameras() {
      return Object.keys(this.selections).some(id => {
        const s = this.selections[id]
        return s.raw || s.conditioned
      })
    }
  },
  methods: {
    isSelected(cameraId) {
      const s = this.selections[cameraId]
      return s && (s.raw || s.conditioned)
    },
    toggleCamera(camera) {
      const s = this.selections[camera.id]
      if (s.raw || s.conditioned) {
        s.raw = false
        s.conditioned = false
      } else {
        s.raw = camera.rawCount > 0
        s.conditioned = camera.conditionedCount > 0
      }
    },
    onCheckboxChange(camera) {
      // reactivity is handled by Vue since selections[id] is already reactive
    },
    buildSelections() {
      const sel = {}
      this.report.forEach(camera => {
        sel[camera.id] = {
          raw: camera.rawCount > 0,
          conditioned: camera.conditionedCount > 0
        }
      })
      this.selections = sel
    },
    addDateRange() {
      this.dateRanges.push({start: null, end: null})
    },
    removeDateRange(index) {
      this.dateRanges.splice(index, 1)
    },
    formatDate(date) {
      if (!date) return null
      const pad = (n) => String(n).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:00`
    },
    getValidDateRanges() {
      const ranges = this.dateRanges
          .filter(r => r.start && r.end)
          .map(r => ({
            start: this.formatDate(r.start),
            end: this.formatDate(r.end)
          }))
      return ranges.length > 0 ? ranges : null
    },
    onCheckVideos() {
      const cameras = this.report.map(({id, name}) => ({id, name}))
      this.$emit('check', {cameras, dateRanges: this.getValidDateRanges()})
    },
    onDelete() {
      const ids = []
      const summaryParts = []

      this.report
          .filter(c => this.isSelected(c.id))
          .forEach(c => {
            const s = this.selections[c.id]
            if (s.raw) ids.push(...c.rawVideoIds)
            if (s.conditioned) ids.push(...c.conditionedVideoIds)

            const parts = []
            if (s.raw && c.rawCount > 0) parts.push(`${c.rawCount} raw files`)
            if (s.conditioned && c.conditionedCount > 0) parts.push(`${c.conditionedCount} conditioned files`)
            if (parts.length > 0) summaryParts.push(`<b>${c.name}</b>: ${parts.join(' and ')}`)
          })

      const summaryLines = summaryParts.join('<br>')

      this.$buefy.dialog.confirm({
        title: 'Are you sure?',
        message: `<p class="has-text-weight-semibold has-text-danger mb-3">Warning: This action is irreversible.</p>
                  <p>You have selected deletion for:</p>
                  <div class="mt-2">${summaryLines}</div>`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        type: 'is-danger',
        onConfirm: () => {
          this.$emit('delete', {ids})
        }
      })
    }
  },
  watch: {
    active(val) {
      if (!val) {
        this.dateRanges = []
        this.selections = {}
      }
    },
    report: {
      handler() {
        this.buildSelections()
      },
      immediate: true
    }
  }
}
</script>

<style scoped>
.camera-card {
  cursor: pointer;
  border: 2px solid transparent;
  transition: border-color 0.2s;
}

.camera-card:hover {
  border-color: #dbdbdb;
}

.camera-card--selected {
  border-color: #3273dc;
}
</style>
